/**
 * 帮助文档查看记录管理工具
 * 用于跟踪用户查看帮助文档的情况
 */

const STORAGE_KEY = "flymeta_help_document_views";

/**
 * 生成文档路径的哈希值（使用简单的字符串哈希算法）
 * @param {string} contentPath - 文档路径
 * @returns {string} 哈希值
 */
export const generateDocumentHash = (contentPath) => {
    let hash = 0;
    if (contentPath.length === 0) return hash.toString();

    for (let i = 0; i < contentPath.length; i++) {
        const char = contentPath.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // 转换为32位整数
    }

    return Math.abs(hash).toString(36); // 转换为36进制字符串
};

/**
 * 获取所有文档查看记录
 * @returns {Object} 查看记录对象
 */
export const getViewRecords = () => {
    try {
        const records = localStorage.getItem(STORAGE_KEY);
        return records ? JSON.parse(records) : {};
    } catch (error) {
        console.error("获取帮助文档查看记录失败:", error);
        return {};
    }
};

/**
 * 获取特定文档的查看记录
 * @param {string} contentPath - 文档路径
 * @returns {Object} 查看记录 { viewed: boolean, viewCount: number, firstViewTime: string, lastViewTime: string }
 */
export const getDocumentViewRecord = (contentPath) => {
    const hash = generateDocumentHash(contentPath);
    const records = getViewRecords();

    return (
        records[hash] || {
            viewed: false,
            viewCount: 0,
            firstViewTime: null,
            lastViewTime: null,
        }
    );
};

/**
 * 记录文档查看
 * @param {string} contentPath - 文档路径
 */
export const recordDocumentView = (contentPath) => {
    try {
        const hash = generateDocumentHash(contentPath);
        const records = getViewRecords();
        const currentTime = new Date().toISOString();

        if (records[hash]) {
            // 已有记录，更新查看次数和最后查看时间
            records[hash].viewCount += 1;
            records[hash].lastViewTime = currentTime;
            records[hash].viewed = true;
        } else {
            // 新记录
            records[hash] = {
                viewed: true,
                viewCount: 1,
                firstViewTime: currentTime,
                lastViewTime: currentTime,
            };
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(records));

        // 触发自定义事件，通知其他组件更新
        window.dispatchEvent(
            new CustomEvent("helpDocumentViewed", {
                detail: { contentPath, hash, record: records[hash] },
            }),
        );
    } catch (error) {
        console.error("记录帮助文档查看失败:", error);
    }
};

/**
 * 检查文档是否已被查看
 * @param {string} contentPath - 文档路径
 * @returns {boolean} 是否已查看
 */
export const isDocumentViewed = (contentPath) => {
    const record = getDocumentViewRecord(contentPath);
    return record.viewed;
};

/**
 * 获取文档查看次数
 * @param {string} contentPath - 文档路径
 * @returns {number} 查看次数
 */
export const getDocumentViewCount = (contentPath) => {
    const record = getDocumentViewRecord(contentPath);
    return record.viewCount;
};

/**
 * 清除所有查看记录（用于测试或重置）
 */
export const clearAllViewRecords = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent("helpDocumentRecordsCleared"));
    } catch (error) {
        console.error("清除帮助文档查看记录失败:", error);
    }
};

/**
 * 清除特定文档的查看记录
 * @param {string} contentPath - 文档路径
 */
export const clearDocumentViewRecord = (contentPath) => {
    try {
        const hash = generateDocumentHash(contentPath);
        const records = getViewRecords();

        if (records[hash]) {
            delete records[hash];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(records));

            window.dispatchEvent(
                new CustomEvent("helpDocumentRecordCleared", {
                    detail: { contentPath, hash },
                }),
            );
        }
    } catch (error) {
        console.error("清除特定帮助文档查看记录失败:", error);
    }
};

/**
 * 获取查看统计信息
 * @returns {Object} 统计信息
 */
export const getViewStatistics = () => {
    const records = getViewRecords();
    const documentHashes = Object.keys(records);

    const totalDocuments = documentHashes.length;
    const totalViews = documentHashes.reduce((sum, hash) => sum + records[hash].viewCount, 0);
    const viewedDocuments = documentHashes.filter((hash) => records[hash].viewed).length;

    return {
        totalDocuments,
        viewedDocuments,
        unviewedDocuments: totalDocuments - viewedDocuments,
        totalViews,
        averageViewsPerDocument: totalDocuments > 0 ? (totalViews / totalDocuments).toFixed(2) : 0,
    };
};
